// P0 · Elegí tu contraseña nueva (decisión D51). Se llega con el enlace de un solo uso que manda el administrador por
// WhatsApp (o el de "¿Olvidaste tu contraseña?"): `/clave#t=<token>`. El token se saca de la dirección apenas se lee;
// la pantalla dice de quién es el enlace, pide la contraseña (8 caracteres o más, sin otras reglas) y entra sola.
import { CLAVE_MINIMA } from '@reparto/shared/nucleo';
import { Boton, Icono, useToast } from '@reparto/ui';
import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { CampoClave } from '../componentes/CampoClave';
import { FormularioAccesoGris } from '../componentes/Esqueletos';
import { AvisoError, type EstadoAcceso, MarcoAcceso } from '../componentes/MarcoAcceso';
import { ErrorApi, SinRed } from '../datos/api';
import {
  ClaveGuardada,
  elegirClave,
  type EnlaceClave,
  problemaDeClave,
  revisarEnlaceClave,
  tomarTokenClave,
} from '../datos/clave';

type Estado =
  | { paso: 'revisando' }
  | { paso: 'elegir'; enlace: EnlaceClave }
  | { paso: 'invalido' }
  | { paso: 'reintentar'; mensaje: string }
  | { paso: 'guardada'; email: string; mensaje: string };

const SIN_RED = 'No hay conexión. Para crear la contraseña necesitás señal.';

export function Clave() {
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const [token] = useState(tomarTokenClave);
  const [estado, setEstado] = useState<Estado>(token ? { paso: 'revisando' } : { paso: 'invalido' });
  const [intento, setIntento] = useState(0);
  const [password, setPassword] = useState('');
  const [errorClave, setErrorClave] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!token) return;
    let vivo = true;
    setEstado({ paso: 'revisando' });
    revisarEnlaceClave(token)
      .then((enlace) => vivo && setEstado({ paso: 'elegir', enlace }))
      .catch((e: unknown) => {
        if (!vivo) return;
        if (e instanceof ErrorApi && (e.estado === 401 || e.estado === 400)) setEstado({ paso: 'invalido' });
        else
          setEstado({
            paso: 'reintentar',
            mensaje: e instanceof SinRed ? SIN_RED : 'No se pudo revisar el enlace. Probá de nuevo.',
          });
      });
    return () => {
      vivo = false;
    };
  }, [token, intento]);

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const p = problemaDeClave(password);
    setErrorClave(p);
    if (p || !token) return;
    setOcupado(true);
    try {
      const s = await elegirClave(token, password);
      mostrar(`Listo, ${s.usuario.nombre.split(' ')[0]}: ya tenés tu contraseña`, { tipo: 'ok' });
      navegar('/', { replace: true });
    } catch (e) {
      if (e instanceof ClaveGuardada) {
        const c = e.causa;
        setEstado({
          paso: 'guardada',
          email: e.email,
          mensaje:
            c instanceof SinRed
              ? 'Cuando tengas señal, entrá con tu correo y la contraseña nueva.'
              : c instanceof ErrorApi
                ? c.message
                : 'Entrá con tu correo y la contraseña nueva.',
        });
      } else if (e instanceof ErrorApi && e.estado === 401) setEstado({ paso: 'invalido' });
      else if (e instanceof SinRed) setError(SIN_RED);
      else if (e instanceof ErrorApi) setError(e.message);
      else setError('No se pudo guardar. Probá de nuevo.');
    } finally {
      setOcupado(false);
    }
  }

  const irAEntrar = (email?: string) => navegar('/acceso', { replace: true, state: { email } satisfies EstadoAcceso });
  const faltan = password && password.length < CLAVE_MINIMA ? CLAVE_MINIMA - password.length : 0;

  return (
    <MarcoAcceso compacto>
      {estado.paso === 'revisando' ? (
        <FormularioAccesoGris texto="Revisando el enlace…" recuadro ayuda />
      ) : estado.paso === 'elegir' ? (
        <form onSubmit={guardar} noValidate className="space-y-4">
          <div>
            <h2 className="text-xl font-medium">Elegí tu contraseña nueva</h2>
            <p className="mt-1 text-[15px] text-stone-600 dark:text-stone-300">
              Hola, {estado.enlace.nombre.split(' ')[0]}. Con esta contraseña entrás con tu correo:
            </p>
            <p className="mt-2 flex items-center gap-2 rounded-2xl bg-stone-100 px-4 py-3 font-medium break-all dark:bg-stone-900">
              <Icono nombre="mail" className="shrink-0 text-stone-500 dark:text-stone-400" />
              {estado.enlace.email}
            </p>
          </div>
          {/* Para que el gestor de contraseñas del celular la guarde con el correo correcto. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={estado.enlace.email}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
          />
          <CampoClave
            etiqueta="Contraseña nueva"
            autoComplete="new-password"
            value={password}
            onChange={(e) => (setPassword(e.target.value), setErrorClave(null))}
            error={errorClave}
            ayuda={
              faltan
                ? `Faltan ${faltan} ${faltan === 1 ? 'carácter' : 'caracteres'}`
                : `${CLAVE_MINIMA} caracteres o más. Puede ser una frase que te acuerdes.`
            }
          />
          {error ? <AvisoError>{error}</AvisoError> : null}
          <Boton type="submit" bloque tamano="lg" icono="check" cargando={ocupado}>
            Guardar y entrar
          </Boton>
        </form>
      ) : estado.paso === 'guardada' ? (
        <div className="space-y-4">
          <h2 className="flex items-center gap-2 text-xl font-medium">
            <Icono nombre="check_circle" className="text-primary-700 dark:text-primary-300" relleno />
            Tu contraseña quedó guardada
          </h2>
          <p className="text-[15px] text-stone-600 dark:text-stone-300">{estado.mensaje}</p>
          <Boton bloque tamano="lg" icono="login" onClick={() => irAEntrar(estado.email)}>
            Ir a entrar
          </Boton>
        </div>
      ) : estado.paso === 'reintentar' ? (
        <div className="space-y-4">
          <h2 className="text-xl font-medium">Elegí tu contraseña nueva</h2>
          <AvisoError>{estado.mensaje}</AvisoError>
          <Boton bloque tamano="lg" icono="refresh" onClick={() => setIntento((n) => n + 1)}>
            Probar de nuevo
          </Boton>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-xl font-medium">Este enlace ya no sirve</h2>
          <p className="text-[15px] text-stone-600 dark:text-stone-300">
            Ya se usó, venció o llegó incompleto. Pedile al administrador que te mande uno nuevo.
          </p>
          <Boton bloque tamano="lg" icono="login" onClick={() => irAEntrar()}>
            Ir a entrar
          </Boton>
          <Boton bloque variante="fantasma" onClick={() => navegar('/acceso/olvide', { replace: true })}>
            ¿Olvidaste tu contraseña?
          </Boton>
        </div>
      )}
    </MarcoAcceso>
  );
}
